/**
 * Legal identity of an invoice issuer (CGI art. 562, 5°): the first and last
 * names of a natural person, the legal form and company name of a legal
 * person, and the register number the issuer is recorded under.
 *
 * `Tenant.legalNature` holds a `LegalNatureCode`; `Tenant.legalForm` holds
 * free text, `LEGAL_FORMS` listing the usual values.
 */
export const LEGAL_NATURES = {
    PERSONNE_PHYSIQUE: { code: "PERSONNE_PHYSIQUE", label: "Personne physique" },
    PERSONNE_MORALE: { code: "PERSONNE_MORALE", label: "Personne morale" },
    ASSOCIATION: { code: "ASSOCIATION", label: "Association" },
};
export const LEGAL_NATURE_CODES = Object.keys(LEGAL_NATURES);
/** True when `value` is one of the `LEGAL_NATURES` codes. */
export function isLegalNature(value) {
    return typeof value === "string" && Object.hasOwn(LEGAL_NATURES, value);
}
/** Usual legal forms, stored and printed by code. */
export const LEGAL_FORMS = [
    { code: "SA", label: "Société anonyme" },
    { code: "SAS", label: "Société par actions simplifiée" },
    { code: "SARL", label: "Société à responsabilité limitée" },
    { code: "SNC", label: "Société en nom collectif" },
    { code: "SCS", label: "Société en commandite simple" },
    { code: "GIE", label: "Groupement d'intérêt économique" },
    { code: "SCOOPS", label: "Société coopérative simplifiée" },
    { code: "SCOOP-CA", label: "Société coopérative avec conseil d'administration" },
    { code: "SC", label: "Société civile" },
];
/** Longest legal form accepted. */
export const LEGAL_FORM_MAX_LENGTH = 60;
/** Longest postal box accepted. */
export const POSTAL_BOX_MAX_LENGTH = 120;
/**
 * Label of the register number (`Tenant.rccm`): an association holds its
 * declaration receipt there, every other issuer its RCCM or trades register
 * number.
 */
export function issuerRegistrationLabel(legalNature) {
    return legalNature === LEGAL_NATURES.ASSOCIATION.code ? "Récépissé de déclaration" : "RCCM";
}
/**
 * The legal form an invoice prints: that of a legal person, or of an
 * association (its nature's label when none is set); none for a natural
 * person or an unknown nature.
 */
export function printedLegalForm(legalNature, legalForm) {
    const form = legalForm?.trim() ?? "";
    if (legalNature === LEGAL_NATURES.PERSONNE_MORALE.code)
        return form === "" ? null : form;
    if (legalNature === LEGAL_NATURES.ASSOCIATION.code) {
        return form === "" ? LEGAL_NATURES.ASSOCIATION.label : form;
    }
    return null;
}
//# sourceMappingURL=legal-identity.js.map